import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';

import { getSampleDisplayState } from '../AnalysisStatus/analysisStatusUtils';

function SampleListItem(props) {
  const {
    sample,
    datasetsGridRef,
    hasAccessReportViruses,
  } = props;

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { ref, inView } = useInView();  

  const [processingState, setProcessingState] = useState(null);
  const [activeAnalysis, setActiveAnalysis] = useState(null);
  const [activeAnalysisDetails, setActiveAnalysisDetails] = useState(null);
  const [activeRun, setActiveRun] = useState(null);

  const sampleUrl = sample ? new URL(sample.attributes.keynome_web_url) : null;

  useEffect(() => {
    if (!isAuthenticated || !inView) {
      return;
    }

    const rowData = datasetsGridRef.current.getRow(sample.attributes.sequencing_run_id)
    setActiveRun(rowData)
  }, [getAccessTokenSilently, isAuthenticated, inView, sample]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (!activeAnalysis) {
      return;
    }

    if (activeAnalysisDetails) {
      return;
    }

    if (!inView) {
      return;
    }

    getAccessTokenSilently()
        .then(accessToken => {
          const headers = new Headers({
            Authorization: `Bearer ${accessToken}`,
          });

          return fetch(
            `${process.env.REACT_APP_KEYNOME_API_URL_BASE}/v1/analyses/${activeAnalysis.id}?extra_fields=positive_species_calls_ct,status,analyzed_with_allow_low_quality`,
            {
              method: 'GET',
              headers,
            },
          )
      })
      .then((res) => res.json())
      .then((response) => {
        if (!response.data) {
          return;
        }

        setActiveAnalysisDetails(response.data);
        if (response.data.attributes.status === 'succeeded') {
          setProcessingState('analysis_ready')
        } else if (response.data.attributes.status === 'failed') {
          setProcessingState('analysis_failed')
        } else if (response.data.attributes.status === 'delayed') {
          setProcessingState('analysis_delayed')
        } else {
          setProcessingState('analysis_running');
        }
      })
      .catch((error) => console.error(error));
  }, [getAccessTokenSilently, isAuthenticated, activeAnalysisDetails, inView, activeAnalysis]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (activeAnalysis) {
      return;
    }

    if (!inView) {
      return;
    }

    getAccessTokenSilently()
        .then(accessToken => {
          const headers = new Headers({
            Authorization: `Bearer ${accessToken}`,
          });

          return fetch(
            `${process.env.REACT_APP_KEYNOME_API_URL_BASE}/v1/lib_seqs/${sample.id}/analyses?primary_only=True`,
            {
              method: 'GET',
              headers,
            },
          )
      })
      .then((res) => res.json())
      .then((response) => {
        if (!response.data.length) {
          setProcessingState('awaiting_analysis');
          return;
        }

        const activeAnalysis = response.data[0];

        setActiveAnalysis(activeAnalysis);
      })
      .catch((error) => console.error(error));
  }, [getAccessTokenSilently, isAuthenticated, inView, activeAnalysis, sample]);

  const primaryAnalysisStatus = activeAnalysisDetails?.attributes?.status || {
    awaiting_analysis: null,
    analysis_ready: 'succeeded',
    analysis_delayed: 'delayed',
    analysis_failed: 'failed',
    analysis_running: 'analyzing',
  }[processingState];
  const sampleDisplayState = getSampleDisplayState({ primaryAnalysisStatus });

  let statusContent = <CircularProgress size={10} />;
  let isClickable = false
  if (processingState) {
    isClickable = sampleDisplayState.isClickable;
    statusContent = (<span style={{ color: sampleDisplayState.color }}><b>{sampleDisplayState.label}</b></span>);
  }

  let yieldContent = <span>-</span>;
  if (activeAnalysis && activeAnalysis.attributes.raw_total_megabases && activeRun) {
    const isIllumina = activeRun.platform === 'illumina'
    const yieldValue = isIllumina ? (activeAnalysis.attributes.raw_total_megabases + activeAnalysis.attributes.raw_total_megabases_r2) : activeAnalysis.attributes.raw_total_megabases

    const yieldAsFormattedString = yieldValue.toLocaleString(
      'en-US',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    );

    yieldContent = (
      <span>
        {yieldAsFormattedString}
        {' '}
        MBs
      </span>
    );
  }

  let speciesIDContent = <span>-</span>;
  if (activeAnalysisDetails) {
    let speciesIDCall = '-';
    let pluralityOfSpeciesIDCall = null;
    if (processingState === 'analysis_ready') {
        speciesIDCall = (!activeRun || (activeRun.platform === 'illumina')) ? '-' : 'Negative'

        let positive_species_calls_ct = activeAnalysisDetails.attributes.positive_species_calls_ct
        if (!hasAccessReportViruses && activeAnalysisDetails.attributes?.positive_species_calls_cts_by_domain) {
          positive_species_calls_ct -= activeAnalysisDetails.attributes.positive_species_calls_cts_by_domain['virus'];
        }
        
        if (positive_species_calls_ct > 0) {
          speciesIDCall = 'Positive';

          pluralityOfSpeciesIDCall = (positive_species_calls_ct === 1) ? ' (monomicrobial)' : ' (polymicrobial)';
        }

    }

    speciesIDContent = (
      <span style={{margin: pluralityOfSpeciesIDCall ? "5px" : null}}>
        <b>
            <small>
          {speciesIDCall}
          {pluralityOfSpeciesIDCall ? null : null}
          {pluralityOfSpeciesIDCall}</small>
        </b>
      </span>
    );
  }

  return (
    <Link ref={ref} to={isClickable ? sampleUrl.pathname : null} style={{ textDecoration: 'none', color: 'black', cursor: isClickable ? 'pointer' : 'default', opacity: isClickable ? 1 : 0.5}}>
      <Box component="li">
        <Grid
          container
          sx={{
              pl: 2, pr: 1, textAlign: 'center', borderBottom: '#bdbdbd 2px solid', '&:hover': { backgroundColor: isClickable ? '#ff980015' : '' },
          }}
        >
          <Grid item xs={4} sx={{ textAlign: 'left' }}>
            <p style={{
                'overflow': 'hidden',
                'whiteSpace': 'nowrap',
                'textOverflow': 'ellipsis',
                margin: '10px 20px 5px 20px'
            }}>
              <b>
                {
                  sample.attributes.sample_name
                  || sample.attributes.sample_id
                  || sample.attributes.library_id
                  || sample.attributes.id
                }
              </b>
            </p>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'center' }}>
            <p style={{
                'overflow': 'hidden',
                'whiteSpace': 'nowrap',
                'textOverflow': 'ellipsis',
                margin: '10px 20px 5px 20px'
            }}>
              <b>
                { statusContent }
              </b>
            </p>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
            <p style={{
                'overflow': 'hidden',
                'whiteSpace': 'nowrap',
                'textOverflow': 'ellipsis',
                margin: '10px 20px 5px 20px'
            }}>
              <b>
                <small style={{color: 'gray', textAlign: 'right'}}>
                {
                  activeRun?.name
                }
                </small>
              </b>
            </p>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'left' }}>
            <p style={{
                'overflow': 'hidden',
                'whiteSpace': 'nowrap',
                'textOverflow': 'ellipsis',
                margin: '10px 20px 5px 20px',
                    color: 'gray'
            }}>
                { yieldContent }
            </p>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'center' }}>
            <p style={{
                'overflow': 'hidden',
                'whiteSpace': 'nowrap',
                'textOverflow': 'ellipsis',
                margin: '10px 20px 5px 20px'
            }}>
                { speciesIDContent }
            </p>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
            <p style={{
                'overflow': 'hidden',
                'whiteSpace': 'nowrap',
                'textOverflow': 'ellipsis',
                margin: '10px 20px 5px 20px'
            }}>
                <small style={{color: 'gray', textAlign: 'right'}}>
                {new Date(sample.attributes.created_at).toLocaleString()}
                </small>
            </p>
        </Grid>
        </Grid>
      </Box>
    </Link>
  );
}

export default SampleListItem;
